import { useState } from 'react';
import { api } from '../lib/api';
import { useAsync } from '../lib/useAsync';
import { Badge, Button, Card, EmptyState, ErrorAlert, PageHeader, Spinner, StatCard } from '../components/ui';
import { formatDate, truncate } from '@kontentowo/shared';

export function AnalyticsPage() {
  const summary = useAsync(() => api.analytics.summary(), []);
  const recent = useAsync(() => api.analytics.recent(30), []);
  const costs = useAsync(() => api.costSummary(), []);
  const [syncing, setSyncing] = useState(false);
  const [syncError, setSyncError] = useState<unknown>(null);
  const [note, setNote] = useState<string | null>(null);

  const sync = async () => {
    setSyncing(true);
    setSyncError(null);
    setNote(null);
    try {
      const res = await api.analytics.sync(60);
      setNote(`Zsynchroizowano: ${res.inserted} wpisów (łącznie ${res.total}).`);
      summary.reload();
      recent.reload();
    } catch (err) {
      setSyncError(err);
    } finally {
      setSyncing(false);
    }
  };

  return (
    <>
      <PageHeader
        title="Analytics"
        subtitle="Metryki opublikowanych postów z Sociamonials (analytics.read) + koszty AI."
        actions={<Button variant="primary" onClick={sync} disabled={syncing}>{syncing ? 'Synchronizacja…' : 'Sync z Sociamonials'}</Button>}
      />
      <ErrorAlert error={syncError} />
      {note && <div className="alert alert--success">{note}</div>}

      {summary.loading && <Spinner />}
      {summary.error ? <ErrorAlert error={summary.error} /> : null}
      {summary.data && (
        <div className="stack">
          <div className="grid grid--stats">
            <StatCard value={summary.data.total} label="Posty (60 dni)" />
            <StatCard value={summary.data.clicks} label="Kliknięcia" />
            <StatCard value={summary.data.likes} label="Lajki" />
            <StatCard value={summary.data.comments} label="Komentarze" />
            <StatCard value={summary.data.shares} label="Udostępnienia" />
            <StatCard value={summary.data.views} label="Wideo odtworzenia" />
            <StatCard value={summary.data.leads} label="Leads" />
            <StatCard value={`$${summary.data.revenue.toFixed(2)}`} label="Revenue" />
          </div>

          {summary.data.byPlatform.length > 0 ? (
            <Card title="Per platforma" flush>
              <table className="table">
                <thead>
                  <tr>
                    <th>Platforma</th>
                    <th>Posty</th>
                    <th>Kliknięcia</th>
                    <th>Lajki</th>
                    <th>Komentarze</th>
                    <th>Udostępnienia</th>
                  </tr>
                </thead>
                <tbody>
                  {summary.data.byPlatform.map((p) => (
                    <tr key={p.platform}>
                      <td style={{ fontWeight: 550 }}>{p.platform}</td>
                      <td>{p.count}</td>
                      <td>{p.clicks}</td>
                      <td>{p.likes}</td>
                      <td>{p.comments}</td>
                      <td>{p.shares}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          ) : (
            <p className="muted">Brak metryk — kliknij „Sync z Sociamonials”.</p>
          )}

          <div className="grid grid--2">
            <Card title="Koszty AI (łącznie)">
              {costs.data ? (
                <div className="stack">
                  <div className="inline">
                    <StatCard value={`$${costs.data.totalCost.toFixed(6)}`} label="Szacowany koszt" />
                    <StatCard value={costs.data.totalGenerations} label="Generacji" />
                  </div>
                  {costs.data.byProvider.map((p) => (
                    <div key={p.providerKey} className="inline" style={{ justifyContent: 'space-between' }}>
                      <Badge tone="neutral">{p.providerKey}</Badge>
                      <span className="muted">{p.count} · ${p.cost.toFixed(6)}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="muted">Brak danych o kosztach.</p>
              )}
            </Card>
          </div>
        </div>
      )}

      <div style={{ marginTop: 20 }}>
        <h2>Ostatnie metryki</h2>
        {recent.loading && <Spinner />}
        {recent.error ? <ErrorAlert error={recent.error} /> : null}
        {recent.data && recent.data.length === 0 && <EmptyState icon="▤" title="Brak metryk" hint="Sync z Sociamonials pobierze dane z ostatnich 60 dni." />}
        {recent.data && recent.data.length > 0 && (
          <Card flush>
            <table className="table">
              <thead>
                <tr>
                  <th>Platforma</th>
                  <th>Post</th>
                  <th>Opublikowano</th>
                  <th>C</th>
                  <th>L</th>
                  <th>K</th>
                  <th>U</th>
                </tr>
              </thead>
              <tbody>
                {recent.data.map((m) => (
                  <tr key={m.id}>
                    <td>
                      <Badge tone="neutral">{m.platform}</Badge>
                    </td>
                    <td>{truncate(m.message ?? m.externalId, 70)}</td>
                    <td className="muted">{m.publishedAt ? formatDate(m.publishedAt) : '—'}</td>
                    <td>{m.clicks}</td>
                    <td>{m.likes}</td>
                    <td>{m.comments}</td>
                    <td>{m.shares}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        )}
      </div>
    </>
  );
}